/**
 * OffCanvasPanel.tsx
 *
 * The one mechanism layout panels use to move off-canvas at narrow widths, so
 * filters, detail panels and sub-navigation do not each hand-roll a drawer.
 *
 * Built on `components/ui/sheet.tsx` rather than a second overlay library. The
 * sheet already ships in the critical-path bundle via the sidebar, so this costs no
 * new bytes, and a second library would mean a second focus trap, a second
 * dismissal layer and a second z-index stack to reconcile.
 *
 * ── Sizing ──────────────────────────────────────────────────────────────────
 * The size map lives here, not in the sheet. The vendored sheet is `w-3/4` with
 * `sm:max-w-sm`, which is 281px at a 375px viewport and never exceeds 384px on a
 * tablet — too narrow for a filter panel, and not a number any call site should
 * have to fight. Keeping the override here also leaves the vendored file untouched,
 * so future upstream updates stay conflict-free.
 *
 * ── Overlay stacking order (KTD13) ──────────────────────────────────────────
 * Declared rather than incidental, because three families already sit at z-50 and a
 * fourth arriving without an agreed order is how a filter sheet ends up behind a
 * floating video call:
 *
 *   z-50        off-canvas panels, the shared modal, the detail right panel
 *   z-[60]      a modal deliberately stacked above another modal
 *   z-[9999]+   toasts, the idle warning, the cookie banner, floating call widgets
 *
 * Panels intentionally stay at the sheet's z-50 rather than climbing above the
 * modal: a modal opened *from* a panel must render above it, and the floating call
 * widgets must stay reachable over anything.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';

// ─── "am I off-canvas?" flag ──────────────────────────────────────────────────

const InOffCanvasContext = createContext(false);

/**
 * True when the calling component is rendered inside an `OffCanvasPanel`.
 *
 * Deliberately returns `false` outside a provider instead of throwing, which is the
 * opposite of `useSidebar`. Panels that read this — the filter sidebar, the detail
 * right panel — render in *both* places: inline on desktop and inside a panel below
 * the breakpoint. Throwing would crash the desktop path, which is the common one.
 *
 * What a reader may change on the strength of this flag is bounded by KTD12: the
 * panel's disclosure state and layout chrome — width, border, collapse affordance,
 * collapsed-versus-expanded — and never the set of fields or data it renders. A
 * panel that shows less content off-canvas than inline is a bug, not a
 * responsive adaptation.
 */
export function useInOffCanvas(): boolean {
  return useContext(InOffCanvasContext);
}

// ─── Region: at most one panel open at a time ─────────────────────────────────

interface RegionApi {
  register: (id: string, close: () => void) => () => void;
  closeOthers: (id: string) => void;
}

const RegionContext = createContext<RegionApi | null>(null);

/**
 * Groups panels so that opening one closes the others (KTD3).
 *
 * Not cosmetic. Two sibling Radix modal dialogs contend over `pointer-events` on
 * <body> and over the focus trap, and the observable result is a page that stops
 * responding to clicks. Enforcing exclusivity structurally means a layout cannot
 * introduce that state by mismanaging two independent booleans.
 *
 * Optional: a panel outside a region still works, it just isn't coordinated.
 */
export function OffCanvasRegion({ children }: { children: ReactNode }) {
  const closers = useRef(new Map<string, () => void>());

  const register = useCallback((id: string, close: () => void) => {
    closers.current.set(id, close);
    return () => {
      closers.current.delete(id);
    };
  }, []);

  const closeOthers = useCallback((id: string) => {
    for (const [key, close] of closers.current) {
      if (key !== id) close();
    }
  }, []);

  const api = useMemo<RegionApi>(() => ({ register, closeOthers }), [register, closeOthers]);

  return <RegionContext.Provider value={api}>{children}</RegionContext.Provider>;
}

// ─── Panel ────────────────────────────────────────────────────────────────────

/**
 * `panel` suits navigation and filters — wide enough to be usable, narrow enough
 * that the list behind stays partly visible, which helps orientation.
 * `wide` suits a record detail, which needs room for label/value pairs.
 * `full` replaces the underlying view entirely; use it when the panel *is* the
 * screen rather than an aside.
 */
export type OffCanvasSize = 'panel' | 'wide' | 'full';

const sizeStyles: Record<OffCanvasSize, string> = {
  panel: 'w-[86vw] max-w-sm sm:max-w-md',
  wide: 'w-[92vw] max-w-md sm:max-w-lg',
  full: 'w-screen max-w-none',
};

export interface OffCanvasPanelProps {
  /** Stable identity within a region. Used only to coordinate exclusivity. */
  id: string;
  isOpen: boolean;
  onClose: () => void;
  /** Which edge it slides from. Filters read as left; details as right. */
  side?: 'left' | 'right';
  /**
   * Accessible name. Required, not optional: Radix warns without one, and a panel
   * announced only as "dialog" is unusable with a screen reader.
   */
  title: string;
  /** Show the title visually as well as to assistive tech. */
  showTitle?: boolean;
  size?: OffCanvasSize;
  className?: string;
  children: ReactNode;
}

export function OffCanvasPanel({
  id,
  isOpen,
  onClose,
  side = 'left',
  title,
  showTitle = true,
  size = 'panel',
  className,
  children,
}: OffCanvasPanelProps) {
  const region = useContext(RegionContext);

  // Held in a ref so registration does not churn every time the parent re-renders
  // with a fresh onClose closure.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!region) return;
    return region.register(id, () => onCloseRef.current());
  }, [region, id]);

  useEffect(() => {
    if (isOpen) region?.closeOthers(id);
  }, [isOpen, region, id]);

  return (
    <Sheet open={isOpen} onOpenChange={(next) => { if (!next) onClose(); }}>
      <SheetContent
        side={side}
        // No description on purpose: the title carries the panel's meaning, and
        // this tells Radix the omission is deliberate rather than forgotten.
        aria-describedby={undefined}
        // gap-0 and p-0 undo the sheet's default spacing so the hosted panel owns
        // its own padding, exactly as it does inline.
        className={cn('gap-0 p-0', sizeStyles[size], className)}
      >
        <SheetHeader
          className={cn(
            'shrink-0 border-b border-slate-200 px-4 py-3',
            !showTitle && 'sr-only',
          )}
        >
          <SheetTitle className="text-sm font-semibold text-slate-900">{title}</SheetTitle>
        </SheetHeader>

        {/*
          The scroll container. pb-safe clears the iOS home indicator so the last
          control in a long filter list stays reachable rather than sitting under
          the system inset.
        */}
        <div className="min-h-0 flex-1 overflow-y-auto pb-safe">
          <InOffCanvasContext.Provider value>{children}</InOffCanvasContext.Provider>
        </div>
      </SheetContent>
    </Sheet>
  );
}
